/**
 * Reusable JSON-LD builders for service landing pages.
 *
 * Returns `<script type="application/ld+json">` objects (Service, FAQPage,
 * BreadcrumbList) for a given service path so each priority service page
 * exposes structured data to Google.
 */

import { canonicalUrl, SITE_URL, SITE_NAME } from "@/lib/seo";

interface ServiceSchemaInput {
  path: string;
  serviceName: string;
  serviceType?: string;
  description: string;
  faqs?: { q: string; a: string }[];
  breadcrumbName?: string;
}

const DEFAULT_FAQS = (serviceName: string) => [
  {
    q: `Where do you install ${serviceName.toLowerCase()}?`,
    a: `We design, fabricate and install ${serviceName.toLowerCase()} across Central Texas, the Hill Country, San Antonio, Austin, the Texas Gulf Coast, South Texas and the Rio Grande Valley from our Seguin, TX shop.`,
  },
  {
    q: `How long do ${serviceName.toLowerCase()} from Texas Made Shade last?`,
    a: `Our welded aluminum frames are built to last 20+ years. Premium Sunbrella® and Tempotest® canvas tops typically hold their color and integrity for 8–12 years in direct Texas sun before needing a re-cover.`,
  },
  {
    q: `How do I get a free quote?`,
    a: `Send measurements and photos through the form on our site or call +1 (830) 390-9588. We respond same business day with sizing questions and a free, no-pressure estimate. On-site measurements are available across our service area.`,
  },
];

export function serviceHeadScripts(input: ServiceSchemaInput) {
  const url = canonicalUrl(input.path);
  const faqs = input.faqs?.length ? input.faqs : DEFAULT_FAQS(input.serviceName);

  const service = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${url}#service`,
    name: `${input.serviceName} — Texas Made Shade`,
    serviceType: input.serviceType ?? input.serviceName,
    description: input.description,
    url,
    provider: { "@id": `${SITE_URL}/#business` },
    brand: { "@type": "Brand", name: SITE_NAME },
    areaServed: [
      { "@type": "State", name: "Texas" },
      { "@type": "City", name: "San Antonio" },
      { "@type": "City", name: "Austin" },
      { "@type": "City", name: "New Braunfels" },
      { "@type": "City", name: "Corpus Christi" },
      { "@type": "City", name: "Seguin" },
    ],
  };

  const faq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${url}#faq`,
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      {
        "@type": "ListItem",
        position: 2,
        name: input.breadcrumbName ?? input.serviceName,
        item: url,
      },
    ],
  };

  return [
    { type: "application/ld+json", children: JSON.stringify(service) },
    { type: "application/ld+json", children: JSON.stringify(faq) },
    { type: "application/ld+json", children: JSON.stringify(breadcrumb) },
  ];
}
